import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
} from '@mui/material'
import { Folder, History } from 'lucide-react'
import { repositoriesAPI, mountsAPI, restoreAPI, archivesAPI } from '../services/api'
import { BorgApiClient } from '../services/borgApi'
import { translateBackendKey, type BackendDetail } from '../utils/translateBackendKey'
import { downloadArchiveFile, downloadArchiveFolder } from '../utils/downloadArchiveFile'
import { invalidateStoredArchives, resyncStoredArchives } from '../utils/archiveResync'
import { waitForArchiveDeleteJob } from '../utils/archiveDeleteJob'
import { usePendingDeletes } from '../hooks/usePendingDeletes'
import { useOperationEvents } from '../hooks/useOperationEvents'
import { SUCCESS_OPERATION_STATUSES } from '../utils/operationStatus'
import RepositorySelectorCard from '../components/RepositorySelectorCard'
import RepositoryStats from '../components/RepositoryStats'
import ArchivesList from '../components/ArchivesList'
import LastRestoreSection from '../components/LastRestoreSection'
import DeleteArchiveDialog from '../components/DeleteArchiveDialog'
import MountArchiveDialog from '../components/MountArchiveDialog'
import { getDefaultMountPoint } from '../utils/mountPoint'
import ArchiveContentsDialog from '../components/ArchiveContentsDialog'
import ArchiveHourlyHeatmap from '../components/archives/ArchiveHourlyHeatmap'
import {
  readStoredScale,
  storeScale,
  suggestScale,
  type HeatmapScale,
} from '../components/archives/heatmapScale'
import StatsFreshness from '../components/StatsFreshness'
import { statsUpdatedAt, statsUpdating } from '../utils/repositoryStats'
import ArchiveSearchField from '../components/archives/ArchiveSearchField'
import ArchiveSeriesHeatmap from '../components/archives/ArchiveSeriesHeatmap'
import ArchiveGrowthChart from '../components/archives/ArchiveGrowthChart'
import { parseBackendDate } from '../utils/dateUtils'
import type { ArchiveRow, HeatmapDay } from '../types/archives'
import type { OperationItem } from '../types/operations'
import { toast } from 'react-hot-toast'
import MountSuccessToast from '../components/MountSuccessToast'
import { Archive, Repository } from '@/types'
import LockErrorDialog from '../components/LockErrorDialog'
import { useAnalytics } from '../hooks/useAnalytics'
import RestoreWizard, { RestoreData } from '../components/RestoreWizard'
import type { RestoreLayout, RestorePathMetadata } from '../utils/restorePaths'
import { getRepoCapabilities, getBorgVersion } from '../utils/repoCapabilities'
import { usePermissions } from '../hooks/usePermissions'
import { useLockBreakPermissions } from '../hooks/useLockBreakPermissions'
import { useTrackedJobOutcomes } from '../hooks/useTrackedJobOutcomes'
import { getArchiveAgeBucket, getJobDurationSeconds } from '../utils/analyticsProperties'

type ArchivesViewMode = 'heatmap' | 'list' | 'growth'

function getInitialViewMode(): ArchivesViewMode {
  const stored = localStorage.getItem('archives-view-mode')
  return stored === 'heatmap' || stored === 'growth' ? stored : 'list'
}

// Downstream actions (restore, mount, delete) key off the borg archive id,
// not the DB row id, so the mapped shape carries `borg_id` as `id`.
function archiveRowToArchive(row: ArchiveRow): Archive {
  return {
    id: row.borg_id,
    archive: row.borg_id,
    name: row.name,
    start: row.start,
    time: row.start,
  }
}

interface RestoreJob {
  id: number
  repository: string
  archive: string
  status: string
  started_at?: string
  completed_at?: string
  error_message?: string
}

function normalizeRepositoryId(value: number | string | null | undefined): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number.parseInt(value, 10)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

// One refetch per burst of index stages (an import or backup chain ends
// archive_sync and stats within seconds).
const STORAGE_REFRESH_DEBOUNCE_MS = 1500
// How often the figures are re-read while the header says "Updating".
const STATS_POLL_MS = 3000

const Archives: React.FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedRepositoryId, setSelectedRepositoryId] = useState<number | null>(() => {
    return normalizeRepositoryId(searchParams.get('repo'))
  })
  const [viewArchive, setViewArchive] = useState<Archive | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<Archive | null>(null)
  const [lockError, setLockError] = useState<{
    repositoryId: number
    repositoryName: string
    borgVersion?: 1 | 2
  } | null>(null)
  const [mountDialogArchive, setMountDialogArchive] = useState<Archive | null>(null)
  const [customMountPoint, setCustomMountPoint] = useState<string>('')

  // Restore functionality
  const [restoreArchive, setRestoreArchive] = useState<Archive | null>(null)
  // A restore started from a search hit already knows its file, so the wizard
  // opens on the destination step with that path selected.
  const [restorePreselection, setRestorePreselection] = useState<{
    paths: string[]
    items: RestorePathMetadata[]
  } | null>(null)
  const [showRestoreWizard, setShowRestoreWizard] = useState<boolean>(false)

  const [viewMode, setViewMode] = useState<ArchivesViewMode>(getInitialViewMode)
  const [chosenScale, setChosenScale] = useState<HeatmapScale | null>(readStoredScale)
  // '' is the whole repository; the growth endpoint restarts its running
  // total when a series is named (spec 4.3).

  const queryClient = useQueryClient()
  const location = useLocation()
  const navigate = useNavigate()
  const { trackArchive, EventAction } = useAnalytics()
  const permissions = usePermissions()
  const canRestore = selectedRepositoryId
    ? permissions.canDo(selectedRepositoryId, 'restore')
    : false

  // Get repositories list
  const { data: repositoriesData, isLoading: loadingRepositories } = useQuery({
    queryKey: ['repositories'],
    queryFn: repositoriesAPI.getRepositories,
  })

  const repositories = React.useMemo(
    () => repositoriesData?.data?.repositories || [],
    [repositoriesData]
  )
  const selectedRepository = React.useMemo(
    () =>
      selectedRepositoryId
        ? repositories.find((r: Repository) => r.id === selectedRepositoryId) || null
        : null,
    [repositories, selectedRepositoryId]
  )
  const { canBreakLock, lockBreakingEnabled } = useLockBreakPermissions({
    repositories,
    fallbackRepositoryId: selectedRepositoryId,
  })

  // The header reads the stored columns; a live `borg info` runs only on
  // its refresh button. The info syncs the archive columns on the server,
  // so the figures are refetched once it has answered.
  const refreshInfoMutation = useMutation({
    mutationFn: async (repository: Repository) => {
      await new BorgApiClient(repository).getInfo()
      // the run that refreshes every figure; it refetches the list and the
      // figures itself, and the caption says "Updating" until it lands
      await resyncStoredArchives(queryClient, repository.id)
    },
    onError: (error: unknown, repository) => {
      const response = (
        error as { response?: { status?: number; data?: { detail?: BackendDetail } } }
      )?.response
      if (response?.status === 423) {
        setLockError({
          repositoryId: repository.id,
          repositoryName: repository.name,
          borgVersion: getBorgVersion(repository),
        })
        return
      }
      toast.error(translateBackendKey(response?.data?.detail))
    },
  })

  // The header's size figures come from the stored `storage` payload
  // (#981): the list carries the stored columns, the storage route adds
  // the archive sums without a live Borg call.
  const { data: repositoryStorageResponse } = useQuery({
    queryKey: ['repository-storage', selectedRepositoryId],
    queryFn: () => repositoriesAPI.getStorage(selectedRepositoryId!),
    enabled: !!selectedRepositoryId,
    retry: false,
    // The event stream refetches this when index work lands; while the
    // header says "Updating" it polls too, so a dropped stream cannot
    // leave the caption stuck.
    refetchInterval: (query) =>
      statsUpdating(query.state.data?.data?.index_pending_kinds, query.state.data?.data?.sync_state)
        ? STATS_POLL_MS
        : false,
  })
  // Until that arrives the list's columns stand in. Only an explicit
  // `null` (the server could not compute the summary) replaces them; a
  // response without the field (an older server) leaves them in place.
  const repositoryStorage =
    repositoryStorageResponse?.data?.storage === undefined
      ? selectedRepository?.storage
      : repositoryStorageResponse.data.storage

  // Get archives for selected repository from the persisted index
  const {
    data: archives,
    isLoading: loadingArchives,
    error: archivesError,
  } = useQuery({
    queryKey: ['repository-archives-stored', selectedRepositoryId],
    queryFn: () => archivesAPI.listStored(selectedRepositoryId!),
    enabled: !!selectedRepositoryId,
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.data?.sync_state === 'syncing' ? STATS_POLL_MS : false,
  })

  const { data: heatmapData } = useQuery({
    queryKey: ['repository-archives-heatmap', selectedRepositoryId],
    queryFn: () => archivesAPI.getHeatmap(selectedRepositoryId!),
    enabled: !!selectedRepositoryId && viewMode === 'heatmap',
    retry: false,
  })

  const { data: growthData } = useQuery({
    queryKey: ['repository-archives-growth', selectedRepositoryId],
    queryFn: () => archivesAPI.getGrowth(selectedRepositoryId!),
    enabled: !!selectedRepositoryId && viewMode === 'growth',
    retry: false,
  })

  // The stored list only changes when archive_sync writes it, so the page
  // refreshes on that operation rather than on a timer: a delete, a prune,
  // a backup's follow-up, and the reconcile tick all land the same way.
  // An index chain ends its stages within seconds of each other, so the
  // header's figures are refetched once per burst, not once per stage.
  // A pending refresh belongs to the repository it was queued for: it is
  // dropped when the selection moves on, not fired against the new one.
  const storageRefreshTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  React.useEffect(
    () => () => {
      if (storageRefreshTimer.current) clearTimeout(storageRefreshTimer.current)
      storageRefreshTimer.current = null
    },
    [selectedRepositoryId]
  )
  const onOperationUpdated = React.useCallback(
    (operation: OperationItem) => {
      if (operation.repository_id !== selectedRepositoryId) return
      if (operation.category === 'index') {
        // every move of index work touches the header: a queued or
        // running stage is the pending state the "indexing" placeholders
        // read, stats and archive_sync write the stored figures at their
        // end, and a failed or cancelled run clears the pending state
        if (storageRefreshTimer.current) clearTimeout(storageRefreshTimer.current)
        storageRefreshTimer.current = setTimeout(() => {
          storageRefreshTimer.current = null
          queryClient.invalidateQueries({ queryKey: ['repository-storage', selectedRepositoryId] })
          queryClient.invalidateQueries({ queryKey: ['repositories'] })
        }, STORAGE_REFRESH_DEBOUNCE_MS)
      }
      if (operation.kind !== 'archive_sync') return
      if (!SUCCESS_OPERATION_STATUSES.has(operation.status)) return
      invalidateStoredArchives(queryClient, selectedRepositoryId as number)
    },
    [queryClient, selectedRepositoryId]
  )
  const onOperationProgress = React.useCallback(() => {}, [])
  useOperationEvents(onOperationUpdated, onOperationProgress)

  // Handle archives error
  React.useEffect(() => {
    const responseStatus = (archivesError as { response?: { status?: number } } | null)?.response
      ?.status
    if (responseStatus === 423 && selectedRepositoryId) {
      setLockError({
        repositoryId: selectedRepositoryId,
        repositoryName: selectedRepository?.name || t('common.unknown'),
        borgVersion: getBorgVersion(selectedRepository),
      })
    }
  }, [archivesError, selectedRepositoryId, selectedRepository, t])

  // Get restore jobs
  const { data: restoreJobsData } = useQuery({
    queryKey: ['restore-jobs'],
    queryFn: restoreAPI.getRestoreJobs,
    refetchInterval: 3000, // Refresh every 3 seconds for live progress
  })

  // Delete archive mutation. The row is marked as deleting from the click
  // until the queued job has finished and the stored list was asked to
  // reconcile (#1197), so a second delete of the same archive cannot start.
  const deletingArchives = usePendingDeletes<string>()
  const deleteArchiveMutation = useMutation({
    mutationFn: ({ archive }: { repository: string; archive: string }) =>
      new BorgApiClient(selectedRepository!).deleteArchive(archive),
    onSuccess: async (data) => {
      // Backend returns a job_id for background deletion. The delete runs
      // asynchronously (agent-delegated deletes take several seconds), so poll
      // the job until it finishes and only then refresh the list: a fixed
      // delay refetched before the archive was actually gone, leaving it in the
      // list until a manual page reload.
      const jobId = data.data.job_id
      toast.success(t('archives.deletionStarted', { id: jobId }))
      setShowDeleteConfirm(null)
      trackArchive(EventAction.DELETE, selectedRepository || undefined)

      const repoId = selectedRepositoryId
      const status = await waitForArchiveDeleteJob(new BorgApiClient(selectedRepository!), jobId)
      if (status === 'failed') {
        toast.error(t('archives.toasts.deleteFailed'))
      }
      // The list is the stored one now, so a refetch alone would return the
      // deleted archive again: ask for a reconcile run and let the event
      // stream refresh the page when archive_sync has caught up.
      await resyncStoredArchives(queryClient, repoId)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('archives.toasts.deleteFailed')
      )
    },
  })

  // Mount archive mutation
  const mountArchiveMutation = useMutation({
    mutationFn: ({
      repository_id,
      archive_name,
      mount_point,
      archive_id,
    }: {
      repository_id: number
      archive_name: string
      mount_point?: string
      archive_id?: string
      archive_start?: string
      is_custom_mount_point: boolean
    }) => mountsAPI.mountBorgArchive({ repository_id, archive_name, mount_point, archive_id }),
    onSuccess: (data, variables) => {
      const mountPoint = data.data.mount_point
      const containerName = 'borg-web-ui'
      const accessCommand = `docker exec -it ${containerName} bash -c "cd ${mountPoint} && bash"`

      toast.custom((t) => <MountSuccessToast toastId={t.id} command={accessCommand} />, {
        duration: 15000,
      })
      trackArchive(EventAction.MOUNT, selectedRepository || undefined, {
        operation: 'mount_archive',
        archive_age_bucket: getArchiveAgeBucket(variables.archive_start),
        uses_custom_mount_point: variables.is_custom_mount_point,
      })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      const errorDetail = translateBackendKey(error.response?.data?.detail) || error.message
      const isMountTimeout = errorDetail.toLowerCase().includes('mount timeout')

      if (isMountTimeout) {
        toast.error(t('archives.mountTimeout'), {
          duration: 10000,
          style: {
            maxWidth: '500px',
          },
        })
      } else {
        toast.error(t('archives.mountFailed', { error: errorDetail }))
      }
    },
  })

  // Restore mutation
  const restoreMutation = useMutation({
    mutationFn: ({
      repository,
      archive,
      destination,
      paths,
      repository_id,
      destination_type,
      destination_connection_id,
      restore_layout,
      path_metadata,
    }: {
      repository: string
      archive: string
      destination: string
      paths: string[]
      repository_id: number
      destination_type: string
      destination_connection_id: number | null
      restore_layout: RestoreLayout
      path_metadata: RestorePathMetadata[]
    }) =>
      restoreAPI.startRestore(
        repository,
        archive,
        paths,
        destination,
        repository_id,
        destination_type,
        destination_connection_id,
        restore_layout,
        path_metadata
      ),
    onSuccess: (_response, variables) => {
      toast.success(t('archives.restoreStarted'), {
        duration: 6000, // Show longer so user can read it
      })
      trackArchive(EventAction.START, selectedRepository || undefined, {
        operation: 'restore',
        destination_type: variables.destination_type,
        restore_layout: variables.restore_layout,
        restore_path_count: variables.paths.length,
        uses_custom_destination: variables.destination !== '/',
        archive_age_bucket: getArchiveAgeBucket(restoreArchive?.start),
      })

      setRestoreArchive(null)
      setShowRestoreWizard(false)

      queryClient.refetchQueries({ queryKey: ['restore-jobs'] })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('archives.toasts.restoreFailed')
      )
    },
  })

  // Handle repository selection
  const handleRepositoryChange = (repositoryId: number) => {
    const normalizedRepositoryId = normalizeRepositoryId(repositoryId)
    setSelectedRepositoryId(normalizedRepositoryId)
    const repo = repositories.find((r: Repository) => r.id === normalizedRepositoryId)

    if (normalizedRepositoryId) {
      setSearchParams({ repo: String(normalizedRepositoryId) }, { replace: true })
    } else {
      setSearchParams({}, { replace: true })
    }
    // Track archive listing (selecting a repo to filter/list its archives)
    if (repo) {
      trackArchive(EventAction.FILTER, repo, { surface: 'archives_page' })
    }
  }

  // Handle archive deletion. For a Borg 2 series the name is ambiguous, so send
  // the archive id (the backend wraps it as aid:<hex>); Borg 1 names are unique.
  const handleDeleteArchive = (archive: Archive) => {
    if (selectedRepository) {
      const archiveRef =
        getBorgVersion(selectedRepository) === 2 && archive.id ? archive.id : archive.name
      // onError already reports the failure.
      void deletingArchives.run(archive.id, () =>
        deleteArchiveMutation
          .mutateAsync({ repository: selectedRepository.path, archive: archiveRef })
          .catch(() => undefined)
      )
    }
  }

  // Handle archive mounting
  const handleMountArchive = () => {
    if (selectedRepositoryId && mountDialogArchive) {
      const defaultMountPoint = getDefaultMountPoint(
        mountDialogArchive,
        selectedRepository?.borg_version
      )
      mountArchiveMutation.mutate({
        repository_id: selectedRepositoryId,
        archive_name: mountDialogArchive.name,
        // Borg 2 series archives share one name; the id addresses exactly one.
        archive_id: mountDialogArchive.id || undefined,
        mount_point: customMountPoint || undefined,
        archive_start: mountDialogArchive.start,
        is_custom_mount_point: !!customMountPoint && customMountPoint !== defaultMountPoint,
      })
      setMountDialogArchive(null)
      setCustomMountPoint('')
    }
  }

  // Open mount dialog
  const openMountDialog = (archive: Archive) => {
    setMountDialogArchive(archive)
    // Pre-fill with the default mount point (sanitised; Borg 2 adds the start time).
    setCustomMountPoint(getDefaultMountPoint(archive, selectedRepository?.borg_version))
  }

  // Open restore wizard directly
  const handleRestoreArchiveClick = React.useCallback(
    (archive: Archive) => {
      setRestoreArchive(archive)
      setRestorePreselection(null)
      setShowRestoreWizard(true)
      trackArchive(EventAction.VIEW, selectedRepository || undefined, {
        surface: 'restore_wizard',
        operation: 'select_archive',
        archive_age_bucket: getArchiveAgeBucket(archive.start),
      })
    },
    [selectedRepository, trackArchive, EventAction]
  )

  // "Restore this" on a file the search found: the version lives in one
  // archive of the repository, so the wizard opens on that archive with the
  // path already selected rather than at the root of the archive page.
  const handleRestoreSearchHit = (archiveId: number, path: string) => {
    // The stored list is where the archive's borg id comes from, and Borg 2
    // needs it: a restore keyed on a name matches every archive of the series.
    // Missing means the list has not arrived or the archive was pruned since
    // the index recorded it, so say that rather than "nothing selected".
    const row = storedArchives.find((archive) => archive.id === archiveId)
    if (!row) {
      toast.error(t('archives.toasts.archiveUnavailable'))
      return
    }
    setRestoreArchive(archiveRowToArchive(row))
    setRestorePreselection({ paths: [path], items: [{ path, type: 'file' }] })
    setShowRestoreWizard(true)
  }

  // Handle restore from wizard
  const handleRestoreFromWizard = (data: RestoreData) => {
    if (!selectedRepository || !restoreArchive) {
      toast.error(t('archives.toasts.notSelected'))
      return
    }

    // Determine destination based on restore strategy
    let destinationPath: string
    if (data.restore_strategy === 'custom' && data.custom_path) {
      destinationPath = data.custom_path
    } else {
      // For "original location", extract to root (/)
      destinationPath = '/'
    }

    // Borg 2 series share a name, so restore must target the specific archive
    // by id (aid:); borg extracts an ambiguous name as "matched N" (rc 4).
    const restoreArchiveRef =
      getBorgVersion(selectedRepository) === 2 && restoreArchive.id
        ? `aid:${restoreArchive.id}`
        : restoreArchive.name

    restoreMutation.mutate({
      repository: selectedRepository.path,
      archive: restoreArchiveRef,
      destination: destinationPath,
      paths: data.selected_paths,
      repository_id: selectedRepository.id,
      destination_type: data.destination_type,
      destination_connection_id: data.destination_connection_id,
      restore_layout: data.restore_layout,
      path_metadata: data.path_metadata,
    })

    setShowRestoreWizard(false)
  }

  useEffect(() => {
    // Handle incoming navigation state (from "View Archives" button)
    const stateRepoId = normalizeRepositoryId(
      (location.state as { repositoryId?: number | string | null } | null)?.repositoryId
    )
    if (stateRepoId) {
      setSelectedRepositoryId(stateRepoId)
      setSearchParams({ repo: String(stateRepoId) }, { replace: true })
      window.scrollTo(0, 0)
    }
  }, [location.state, setSearchParams])

  const storedArchives = (archives?.data?.archives || [])
    .slice()
    .sort((a: ArchiveRow, b: ArchiveRow) => {
      return parseBackendDate(b.start).getTime() - parseBackendDate(a.start).getTime()
    })
  const archivesList = storedArchives.map(archiveRowToArchive)
  // The list carries the borg id; the detail route wants the row id. The
  // row renders it as a link, so the page hands over the route itself.
  const archiveHref = (archive: { id: string }) => {
    const row = storedArchives.find((a: ArchiveRow) => a.borg_id === archive.id)
    return row ? `/archives/${selectedRepositoryId}/${row.id}` : undefined
  }
  const syncState = archives?.data?.sync_state ?? 'never'
  const lastSyncedAt = archives?.data?.last_synced_at ?? null
  // The list arrives newest first, so the first row of each series is that
  // series' head. "Present in latest" is per series, not per repository.
  const newestArchiveIdBySeries = React.useMemo(() => {
    const heads: Record<string, number> = {}
    for (const row of storedArchives) {
      if (!(row.series in heads)) heads[row.series] = row.id
    }
    return heads
  }, [storedArchives])
  const heatmapScale: HeatmapScale = chosenScale ?? suggestScale(storedArchives)

  const handleSelectHeatmapDay = (day: HeatmapDay) => {
    if (!selectedRepositoryId || day.archive_ids.length === 0) return
    navigate(`/archives/${selectedRepositoryId}/${day.archive_ids[0]}`)
  }

  // Get last restore job for selected repository
  const lastRestoreJob = React.useMemo(() => {
    if (!selectedRepository || !restoreJobsData?.data?.jobs) return null

    // Filter restore jobs for this repository and get the most recent one
    const repoJobs = restoreJobsData.data.jobs.filter(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (job: any) => job.repository === selectedRepository.path
    )

    return repoJobs.length > 0 ? repoJobs[0] : null
  }, [selectedRepository, restoreJobsData])

  // Handle viewing archive contents
  const handleViewArchive = (archive: Archive) => {
    setViewArchive(archive)
    trackArchive(EventAction.VIEW, selectedRepository || undefined, {
      surface: 'archive_contents',
      operation: 'open_archive',
      archive_age_bucket: getArchiveAgeBucket(archive.start),
    })
  }

  const handleRestoreArchive = (archive: Archive) => {
    // Open restore wizard flow instead of navigating to separate page
    handleRestoreArchiveClick(archive)
  }

  useTrackedJobOutcomes<RestoreJob>({
    jobs: restoreJobsData?.data?.jobs,
    onTerminal: (job) => {
      const action =
        job.status === 'completed' || job.status === 'completed_with_warnings'
          ? EventAction.COMPLETE
          : EventAction.FAIL
      // A Borg 2 restore stores the aid:<id> selector as job.archive, so match
      // on the id too — otherwise the name lookup misses and the age bucket is lost.
      const archiveStart = archivesList.find(
        (archive: Archive) => archive.name === job.archive || `aid:${archive.id}` === job.archive
      )?.start

      trackArchive(action, selectedRepository ?? job.repository, {
        operation: 'restore',
        job_id: job.id,
        status: job.status,
        archive_age_bucket: getArchiveAgeBucket(archiveStart),
        duration_seconds: getJobDurationSeconds(job.started_at, job.completed_at),
        error_present: !!job.error_message,
      })
    },
  })

  const panelSx = {
    borderRadius: 3,
    border: '1px solid',
    borderColor: isDark ? alpha('#fff', 0.07) : alpha('#000', 0.07),
    // The card owns its surface. Without it the page background shows through
    // and any child that needs an opaque backdrop (the heatmap's sticky label
    // column) paints a paper-coloured rectangle inside the card.
    bgcolor: 'background.paper',
    overflow: 'hidden',
  }

  const scaleToggle = (
    <ToggleButtonGroup
      value={heatmapScale}
      exclusive
      size="small"
      aria-label={t('archives.view.scaleLabel')}
      onChange={(_event, value: HeatmapScale | null) => {
        if (!value) return
        setChosenScale(value)
        storeScale(value)
      }}
    >
      <ToggleButton value="days">{t('archives.view.scaleDays')}</ToggleButton>
      <ToggleButton value="hours">{t('archives.view.scaleHours')}</ToggleButton>
    </ToggleButtonGroup>
  )

  return (
    <Box>
      {/* ── Compact header: title + repo selector inline ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              lineHeight: 1.3,
            }}
          >
            {t('archives.title')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('archives.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ width: { xs: '100%', sm: '340px' }, flexShrink: 0 }}>
          <RepositorySelectorCard
            repositories={repositories}
            value={selectedRepositoryId}
            onChange={(v) => handleRepositoryChange(v as number)}
            loading={loadingRepositories}
            sx={{ mb: 0 }}
          />
        </Box>
      </Box>

      {/* ── No repository selected ── */}
      {!selectedRepositoryId && !loadingRepositories && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 8,
            color: 'text.secondary',
          }}
        >
          <Folder size={48} style={{ marginBottom: 16 }} />
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
            }}
          >
            {repositories.length === 0
              ? t('archives.noRepositories')
              : t('archives.selectRepository')}
          </Typography>
        </Box>
      )}

      {/* ── Context panel: stats + last restore ── */}
      {selectedRepositoryId && (selectedRepository || lastRestoreJob) && (
        <Box sx={{ ...panelSx, mb: 3 }}>
          {/* Stats */}
          {selectedRepository && (
            <Box sx={{ p: 2.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.5,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {t('repositoryStats.heading')}
                </Typography>
                <StatsFreshness
                  updatedAt={statsUpdatedAt(repositoryStorage, lastSyncedAt)}
                  syncState={syncState}
                  updating={
                    refreshInfoMutation.isPending ||
                    statsUpdating(
                      repositoryStorageResponse?.data?.index_pending_kinds ??
                        selectedRepository.index_pending_kinds,
                      syncState
                    )
                  }
                  onRefresh={() => refreshInfoMutation.mutate(selectedRepository)}
                />
              </Box>
              <RepositoryStats
                // the way out of the used-on-disk figure: what a prune would give back
                freeSpaceHref={
                  permissions.canDo(selectedRepositoryId, 'maintenance') &&
                  getRepoCapabilities(selectedRepository).canPrune
                    ? `/repositories/${selectedRepositoryId}/prune-preview`
                    : undefined
                }
                storage={repositoryStorage}
                // a list that could not be read is no count of zero
                archiveCount={archivesError ? null : archivesList.length}
                archivesLoading={!archives && !archivesError}
                indexPendingKinds={
                  repositoryStorageResponse?.data?.index_pending_kinds ??
                  selectedRepository.index_pending_kinds
                }
              />
            </Box>
          )}
          {/* Last Restore, only when there is one to show */}
          {lastRestoreJob && (
            <Box
              sx={{
                px: 2.5,
                py: 2,
                borderTop: '1px solid',
                borderColor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.06),
                bgcolor: isDark ? alpha('#fff', 0.012) : alpha('#000', 0.01),
              }}
            >
              <LastRestoreSection restoreJob={lastRestoreJob} />
            </Box>
          )}
        </Box>
      )}

      {/* ── Archives list ── */}
      {selectedRepositoryId && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              mb: 2,
            }}
          >
            <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
              <ArchiveSearchField
                repositoryId={selectedRepositoryId}
                newestArchiveIdBySeries={newestArchiveIdBySeries}
                onRestorePath={canRestore ? handleRestoreSearchHit : undefined}
              />
            </Box>
            <Tooltip title={t('archives.toolbarOperations')}>
              <IconButton
                component={RouterLink}
                to={`/activity?repository_id=${selectedRepositoryId}`}
                size="small"
                aria-label={t('archives.toolbarOperations')}
                sx={{ ml: { sm: 'auto' }, flexShrink: 0 }}
              >
                <History size={18} />
              </IconButton>
            </Tooltip>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              size="small"
              onChange={(_event, value: ArchivesViewMode | null) => {
                if (!value) return
                setViewMode(value)
                localStorage.setItem('archives-view-mode', value)
              }}
            >
              <ToggleButton value="list">{t('archives.view.list')}</ToggleButton>
              <ToggleButton value="heatmap">{t('archives.view.heatmap')}</ToggleButton>
              <ToggleButton value="growth">{t('archives.view.growth')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {viewMode === 'heatmap' ? (
            heatmapData?.data ? (
              <Box sx={{ ...panelSx, p: 2.5 }}>
                {heatmapScale === 'hours' ? (
                  <ArchiveHourlyHeatmap
                    archives={storedArchives}
                    header={{ toolbar: scaleToggle }}
                    onSelectArchive={(archiveId) =>
                      navigate(`/archives/${selectedRepositoryId}/${archiveId}`)
                    }
                  />
                ) : (
                  <ArchiveSeriesHeatmap
                    data={heatmapData.data}
                    header={{ toolbar: scaleToggle }}
                    onSelectDay={handleSelectHeatmapDay}
                    onSelectArchive={(archiveId) =>
                      navigate(`/archives/${selectedRepositoryId}/${archiveId}`)
                    }
                    archiveLookup={(archiveId) => {
                      const row = storedArchives.find((a) => a.id === archiveId)
                      return row
                        ? { name: row.name, start: row.start, size: row.deduplicated_size }
                        : undefined
                    }}
                  />
                )}
              </Box>
            ) : null
          ) : viewMode === 'growth' ? (
            growthData?.data ? (
              <Box sx={{ ...panelSx, p: 2.5 }}>
                <ArchiveGrowthChart
                  data={growthData.data}
                  onSelectArchive={(archiveId) =>
                    navigate(`/archives/${selectedRepositoryId}/${archiveId}`)
                  }
                />
              </Box>
            ) : null
          ) : (
            <ArchivesList
              archives={archivesList}
              repositoryName={selectedRepository?.name || ''}
              loading={loadingArchives}
              onViewArchive={handleViewArchive}
              onOpenArchive={(archive) => {
                const href = archiveHref(archive)
                if (href) navigate(href)
              }}
              archiveHref={archiveHref}
              onRestoreArchive={canRestore ? handleRestoreArchive : undefined}
              onMountArchive={openMountDialog}
              onDeleteArchive={(archive) => setShowDeleteConfirm(archive)}
              deletingArchiveIds={deletingArchives.pending}
              mountDisabled={mountArchiveMutation.isPending}
              canDelete={
                getRepoCapabilities({ mode: selectedRepository?.mode }).canDeleteArchive &&
                (selectedRepositoryId
                  ? permissions.canDo(selectedRepositoryId, 'delete_archive')
                  : false)
              }
            />
          )}
        </>
      )}

      {/* View Contents Modal */}
      <ArchiveContentsDialog
        open={!!viewArchive}
        archive={viewArchive}
        repository={selectedRepository ?? null}
        storedArchives={storedArchives}
        onClose={() => setViewArchive(null)}
        onDownloadFile={(archiveName, filePath, size) => {
          if (selectedRepository) {
            trackArchive(EventAction.DOWNLOAD, selectedRepository, {
              operation: 'download_archive_file',
              archive_age_bucket: getArchiveAgeBucket(viewArchive?.start),
            })
            const archiveRef =
              getBorgVersion(selectedRepository) === 2
                ? (viewArchive?.id ?? archiveName)
                : archiveName
            return downloadArchiveFile(selectedRepository, archiveRef, filePath, {
              totalSize: size ?? undefined,
            })
          }
        }}
        onDownloadFolder={(archiveName, folderPath) => {
          if (selectedRepository) {
            trackArchive(EventAction.DOWNLOAD, selectedRepository, {
              operation: 'download_archive_folder',
              archive_age_bucket: getArchiveAgeBucket(viewArchive?.start),
            })
            const archiveRef =
              getBorgVersion(selectedRepository) === 2
                ? (viewArchive?.id ?? archiveName)
                : archiveName
            return downloadArchiveFolder(selectedRepository, archiveRef, folderPath)
          }
        }}
      />

      {/* Mount Archive Dialog */}
      <MountArchiveDialog
        open={!!mountDialogArchive}
        archive={mountDialogArchive}
        mountPoint={customMountPoint}
        onMountPointChange={setCustomMountPoint}
        onClose={() => setMountDialogArchive(null)}
        onConfirm={handleMountArchive}
        mounting={mountArchiveMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteArchiveDialog
        open={!!showDeleteConfirm}
        archiveName={showDeleteConfirm?.name ?? null}
        onClose={() => setShowDeleteConfirm(null)}
        onConfirm={() => showDeleteConfirm && handleDeleteArchive(showDeleteConfirm)}
        deleting={showDeleteConfirm ? deletingArchives.isDeleting(showDeleteConfirm.id) : false}
      />

      {/* Lock Error Dialog */}
      {lockError && (
        <LockErrorDialog
          open={!!lockError}
          onClose={() => setLockError(null)}
          repositoryId={lockError.repositoryId}
          repositoryName={lockError.repositoryName}
          borgVersion={lockError.borgVersion}
          canBreakLock={canBreakLock({ repository_id: lockError.repositoryId })}
          lockBreakingEnabled={lockBreakingEnabled}
          onLockBroken={() => {
            // Invalidate queries to retry
            queryClient.invalidateQueries({
              queryKey: ['repository-archives-stored', lockError.repositoryId],
            })
          }}
        />
      )}

      {/* Restore Wizard */}
      {restoreArchive && selectedRepository && (
        <RestoreWizard
          open={showRestoreWizard}
          onClose={() => setShowRestoreWizard(false)}
          archive={restoreArchive}
          repository={selectedRepository}
          repositoryType={selectedRepository.repository_type || 'local'}
          onRestore={handleRestoreFromWizard}
          initialSelectedPaths={restorePreselection?.paths}
          initialSelectedItems={restorePreselection?.items}
        />
      )}
    </Box>
  )
}

export default Archives
